import React, { FC, useEffect, useState } from "react";
import "./style.scss";
import { useTranslation } from "react-i18next";
import Close from "../../../../assets/Close.svg";
import CheckMark from "../../../../assets/CurrencyCheckMark.svg";
import { CURRENCY_FLAGS } from "static/currencies";
import { timer } from "helpers/functions";
import Deposit from "../../../../assets/DetailsDeposit.svg";
import Exchange from "../../../../assets/DetailsExchange.svg";
import Withdraw from "../../../../assets/DetailsWithdrawal.svg";
import InfiniteScroll from "react-infinite-scroll-component";
import { PATH_LIST } from "shared/lib/react-router/paths";
import { useNavigate } from "react-router-dom";
import { GetCurrByCode } from "helpers/currencies";
import { useStore } from "zustand";
import { fxOrderStore } from "entities/fx-order";
import { accountStore } from "entities/account";
import Loading, { Spinner } from "widgets/loading";
import moment from "moment";
import { AssetType, OperationStatus, OperationType } from "static/types";
import useSubAccount from "hooks/useSubAccount";

type HistoryItemProps = {
  type: string;
  amount: string;
  date: string;
  amount2?: string | false;
};

const HistoryItem: FC<HistoryItemProps> = ({ type, amount, date, amount2 }) => {
  return (
    <div className="balance-details-bottomsheet__history__content__item">
      <div className="balance-details-bottomsheet__history__content__item__left">
        <p>{type}</p>
        <span>{date}</span>
      </div>
      <div className="balance-details-bottomsheet__history__content__item__right">
        <p>{amount}</p>
        {amount2 &&
          !amount2.includes("NaN") &&
          !amount2.includes("undefined") && <span>{amount2}</span>}
      </div>
    </div>
  );
};

interface BalanceDetailsBottomsheetProps {
  handleClose: () => void;
  code: string;
  amount: string;
}

const BalanceDetailsBottomsheet: FC<BalanceDetailsBottomsheetProps> = ({
  handleClose,
  code,
  amount,
}) => {
  const { t, i18n } = useTranslation("app");
  const navigate = useNavigate();
  const { setFrom, clear } = useStore(fxOrderStore);
  const {
    isLoading,
    currencyActivities,
    getCurrencyActivities,
    currencyActivitiesCount,
    selectCurrency,
  } = useStore(accountStore);
  const [page, setPage] = useState(0);
  const limit = 25;
  const symbol = GetCurrByCode(code);

  const { subAccount } = useSubAccount();

  const btns = subAccount
    ? []
    : [
        {
          name: "Deposit",
          icon: Deposit,
          onClick: () => {
            // selectCurrency("deposit")(code);
            navigate(PATH_LIST.deposit, {
              state: { currency: code },
            });
          },
        },
        {
          name: "Exchange",
          icon: Exchange,
          onClick: () => {
            clear();
            setFrom(code);
            navigate(PATH_LIST.currencies);
          },
        },
        {
          name: "Withdraw",
          icon: Withdraw,
          onClick: () => {
            selectCurrency("withdraw")(code);
            navigate(PATH_LIST.withdrawal);
          },
        },
      ];

  useEffect(() => {
    getCurrencyActivities(code, page, limit);
  }, [code, page]);

  // useEffect(() => {
  //   console.log(currencyActivities);
  // }, [currencyActivities]);

  const getTotalAmount = (item: any): number => {
    let total = 0;
    if (
      item.status === OperationStatus.Completed &&
      item.specific_data?.amount
    ) {
      total = item.specific_data.amount;
    } else {
      total = item.price * item.quantity_complited;
    }

    if (item.operation === OperationType.Buy) {
      total = total + Math.abs(item.commission_completed);
    } else {
      total = total - Math.abs(item.commission_completed);
    }

    return +total.toFixed(2);
  };

  const itemComment = (item: any, symbol: string) => {
    if (item.comment && item.comment.includes("Payment of interest")) {
      return `${t("interestPayment")} ${symbol && `(${symbol})`}`;
    } else if (item.comment && item.comment.includes("Dividend payment")) {
      return `${t("dividendPayment")} ${symbol && `(${symbol})`}`;
    } else if (item.comment && item.comment.includes("Coupon payment")) {
      return `${t("couponPayment")} ${symbol && `(${symbol})`}`;
    } else if (item.comment && item.comment.includes("Bond redemption")) {
      return `${t("bondRedemption")} ${symbol && `(${symbol})`}`;
    } else if (item.comment && item.comment.includes("Deposit")) {
      return t("deposit");
    } else if (item.comment && item.comment.includes("Withdrawal")) {
      return t("withdrawal");
    }
  };

  const itemType = (item: any) => {
    if (item.asset?.type && item.asset?.type === AssetType.Currency) {
      return t("exchange");
    } else if (item.t === "payment") {
      if (item.type === "dividend") {
        return t("dividendPayment") + ` (${item?.asset.symbol})`;
      } else if (item.type === "coupon") {
        return t("couponPayment") + ` (${item?.asset.symbol})`;
      } else if (item.type === "nominal") {
        return t("bondRedemption") + ` (${item?.asset.symbol})`;
      } else if (item.type === "deposit") {
        return t("deposit");
      } else if (item.type === "withdrawal") {
        return t("withdrawal");
      } else {
        return itemComment(item, item.asset?.symbol || "") || t(item.t);
      }
    } else if (item.asset?.type && item.asset?.type !== AssetType.Currency) {
      return item.operation === OperationType.Sell ? t("sell") : t("buy");
    } else return t(item.t);
  };

  const formatDate = (date: string) => {
    return moment(date).locale(i18n.language).format("DD MMM YYYY HH:mm");
  };

  const getTotalProceed = (item: any) => {
    if (item.status === OperationStatus.Completed) {
      if (item.specific_data?.amount) {
        return item.specific_data.amount;
      }
    }

    let total = item.price * item.quantity_complited;

    return +total.toFixed(2);
  };

  const getCharacter = (item: any) => {
    const isGeneral = GetCurrByCode(code)?.symbol === item.symbol;
    // console.log(isGeneral, item.symbol, code);
    if (isGeneral) {
      return item.operation === OperationType.Sell ? "-" : "+";
    } else {
      return item.operation === OperationType.Sell ? "+" : "-";
    }
  };
  const getCharacterReverse = (item: any) => {
    if (getCharacter(item) === "+") {
      return "-";
    } else {
      return "+";
    }
  };

  const amount1 = (item: any) => {
    if (item.t === "payment") {
      return `+ ${+item.amount.toFixed(2)}${item.currency.symbol}`;
    } else if (item.asset?.type) {
      if (item.asset?.type === AssetType.Currency) {
        if (GetCurrByCode(code)?.symbol === item.currency.symbol) {
          return `${getCharacter(item)} ${getTotalAmount(item)}${
            item.currency.symbol
          }`;
        } else {
          return `${getCharacterReverse(item)} ${item.quantity_complited}${
            GetCurrByCode(item.asset.symbol)?.symbol
          }`;
        }
      } else {
        return `${item.operation === OperationType.Sell ? "+" : "-"}
        ${
          item.specific_data?.accrued_interest
            ? getTotalAmount(item) +
              item.specific_data?.accrued_interest * item.quantity_complited
            : getTotalAmount(item)
        }${item.currency.symbol}`;
      }
    } else {
      return `${item.comment.includes("Withdrawal") ? "-" : "+"} ${
        item.amount
      }${symbol?.symbol}`;
    }
  };

  const amount2 = (item: any) => {
    if (item.asset?.type) {
      if (item.asset?.type === AssetType.Currency) {
        if (GetCurrByCode(code)?.symbol === item.currency.symbol) {
          return `${getCharacterReverse(item)} ${item.quantity_complited}${
            GetCurrByCode(item.asset.symbol)?.symbol
          }`;
        } else {
          return `${getCharacter(item)} ${getTotalAmount(item)}${
            item.currency.symbol
          }`;
        }
      } else {
        return `${item.operation === OperationType.Sell ? "-" : "+"} 
        ${item.quantity_complited} ${
          item.asset.symbol
          // item.quantity == 1 ? t("unit") : " " + t("units2")
        }`;
      }
    } else {
      return;
    }
  };

  return (
    <div
      className="balance-details-bottomsheet-overlay"
      onClick={(e) => {
        e.stopPropagation();
        handleClose();
      }}
    >
      <div
        className="balance-details-bottomsheet"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <div className="balance-details-bottomsheet__header">
          <div></div>
          <img
            style={{ cursor: "pointer" }}
            src={Close}
            alt="close"
            onClick={handleClose}
          />
        </div>

        <div className="balance-details-bottomsheet__balance">
          <span>
            {code} {t("available")}
          </span>
          <p>
            {amount} {symbol?.symbol}
          </p>
        </div>

        <div className="balance-details-bottomsheet__btns">
          {btns.map((btn, i) => (
            <button key={i} onClick={btn.onClick}>
              <img src={btn.icon} alt={btn.name} />
              <p>{btn.name}</p>
            </button>
          ))}
        </div>

        <div
          className="balance-details-bottomsheet__history"
          id="scrollableDiv"
        >
          <p className="balance-details-bottomsheet__history__title">
            {code} {t("transactions")}
          </p>

          {isLoading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginBlock: "32px 56px",
              }}
            >
              <Spinner />
            </div>
          ) : (
            <InfiniteScroll
              className="balance-details-bottomsheet__history__content"
              dataLength={currencyActivities.length}
              next={() => {
                setPage(page + 1);
              }}
              hasMore={currencyActivities.length < currencyActivitiesCount}
              loader={
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <Spinner />
                </div>
              }
              scrollableTarget="scrollableDiv"
            >
              {currencyActivities.map((item, i) => (
                <HistoryItem
                  key={i}
                  type={itemType(item)}
                  amount={amount1(item)}
                  amount2={amount2(item)}
                  date={formatDate(
                    item.asset?.type ? item.order_close_time : item.updatedAt
                  )}
                />
              ))}
            </InfiniteScroll>
          )}
        </div>
      </div>
    </div>
  );
};

export default BalanceDetailsBottomsheet;
